import type {
  AnalyticsEventName,
  AnalyticsEventPayloads,
  AnalyticsPersonProperties,
} from "@mytour/shared";
import posthog from "posthog-js";
import {isAnalyticsInitialized, isCaptureEnabled} from "./analytics.js";
import {authenticationApiService} from "./authentication/authenticationApi.service.js";

/**
 * Typed frontend wrapper around posthog-js. Never call the SDK directly:
 * the contract in @mytour/shared is the single source of truth for event
 * names and payloads.
 *
 * Two guards, intentionally distinct (see analytics.ts): `track` and the
 * replay toggles are gated on `isCaptureEnabled()` (prod/dev only), while
 * `identify` runs wherever the SDK is initialized — but it routes through a
 * non-capturing path on canary/latest and in moderation, so flag targeting
 * works there without emitting any SDK event (see `identify`).
 *
 * Moderation gate (support staff impersonating a customer via "Login as"):
 * `track` and `startSessionReplay` are no-op when the `jhi-moderation`
 * localStorage flag is set (read through `authenticationApiService.isModeration()`
 * — the single owner of the key). This is the *structural* emission boundary:
 * every current and future call site is covered here, not only the
 * `useAnalyticsSession` watcher. Without it a support session would flip
 * funnel milestones and record support activity. `stopSessionReplay` is
 * intentionally NOT gated — a replay started before the flag was set must
 * still be stoppable when moderation turns on mid-session.
 *
 * `identify` in moderation never does a real `posthog.identify()` (the
 * support session's distinct id must never merge into the client's person),
 * but still feeds the client's properties through the non-capturing
 * `setPersonPropertiesForFlags` path. Scope of what that buys: flags targeted
 * on a person property (companyId, plan, …) resolve as they do for the client.
 * Targeting that keys on the identity itself — percentage rollout, cohort,
 * distinct id — still resolves against whatever distinct id the SDK holds for
 * this browser, not the client's.
 */
export const analyticsService = {
  /** Sends one explicit analytics event (prod/dev only, never in moderation). */
  track<E extends AnalyticsEventName>(event: E, props: AnalyticsEventPayloads[E]): void {
    if (authenticationApiService.isModeration() || !isCaptureEnabled()) {
      return;
    }
    posthog.capture(event, props);
  },

  /**
   * Binds the PostHog person to our account id — the cross-stage distinct id.
   *
   * Three paths, checked in this order:
   * - moderation: `setPersonPropertiesForFlags` only, whatever the capture
   *   mode. `distinctId` is dropped on purpose — binding it would merge the
   *   support session into the client's person (see the moderation gate above).
   * - capture on (prod/dev): a real `identify`, which captures `$identify`/`$set`
   *   and enables targeting by distinct id, by person property, and experiment
   *   continuity.
   * - capture off (canary/latest): the **non-capturing** flags path
   *   `setPersonPropertiesForFlags` — it feeds the traits into the flag
   *   evaluation request (and reloads flags) without emitting any event or
   *   creating a person in the project. The distinct id stays anonymous, so
   *   flag targeting there is by person property (companyId, plan, …) or by
   *   percentage, not by account id. This keeps flags-only mode truly free of
   *   SDK events.
   */
  identify(distinctId: string, props?: AnalyticsPersonProperties): void {
    if (!isAnalyticsInitialized()) {
      return;
    }
    if (authenticationApiService.isModeration()) {
      if (props) {
        posthog.setPersonPropertiesForFlags(props);
      }
      return;
    }
    if (isCaptureEnabled()) {
      posthog.identify(distinctId, props);
      return;
    }
    if (props) {
      posthog.setPersonPropertiesForFlags(props);
    }
  },

  /**
   * Starts session replay. Overrides the `disable_session_recording` init
   * flag (documented posthog-js behavior); inputs stay masked via the
   * `session_recording.maskAllInputs` init option.
   */
  startSessionReplay(): void {
    if (authenticationApiService.isModeration() || !isCaptureEnabled()) {
      return;
    }
    posthog.startSessionRecording();
  },

  /** Stops session replay (the onboarding scope ended). */
  stopSessionReplay(): void {
    if (!isCaptureEnabled()) {
      return;
    }
    posthog.stopSessionRecording();
  },
};
